import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  FaUsers,
  FaMapMarkerAlt,
  FaComments,
  FaChartBar,
  FaHistory,
  FaArrowUp,
  FaArrowDown,
  FaExclamationTriangle,
  FaSync,
  FaSpinner,
  FaCheckCircle,
  FaClock,
  FaBullhorn,
  FaUserCheck
} from "react-icons/fa";

const StateDashboard = () => {
  const { user, token } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const apiBaseUrl = process.env.REACT_APP_API_URL || "https://api.tinubu-ambode.org";
      const apiUrl = `${apiBaseUrl}/api/v1/analytics/dashboard`;
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      const authToken = token || localStorage.getItem('token') || localStorage.getItem('authToken');
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: headers,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || "API request failed");
      }
      
      setDashboardData(result.data);
      
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const container = {
    width: "100%",
    minHeight: "100vh",
    fontFamily: "Inter, sans-serif",
    backgroundColor: "#f8fafc",
    padding: "0",
    margin: "0",
  };

  const contentWrapper = {
    width: "100%",
    maxWidth: "100%",
    padding: "15px",
    boxSizing: "border-box",
  };

  const header = {
    marginBottom: "25px",
    padding: "0 5px",
  };

  const title = {
    fontSize: "22px",
    fontWeight: "700",
    color: "#2563eb",
    marginBottom: "8px",
    lineHeight: "1.3",
  };

  const subtitle = {
    fontSize: "14px",
    color: "#6b7280",
    lineHeight: "1.5",
    margin: "0",
  };

  const statGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "15px",
    marginBottom: "25px",
    width: "100%",
  };

  const statCard = {
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    padding: "20px",
    border: "1px solid #e5e7eb",
    width: "100%",
    boxSizing: "border-box",
    minHeight: "140px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  };

  const statTitle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: "#2563eb",
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "10px",
  };

  const statValue = {
    fontSize: "24px",
    fontWeight: "700",
    color: "#111827",
    marginBottom: "6px",
  };

  const statSubValue = {
    fontSize: "14px",
    color: "#6b7280",
    fontWeight: "500",
  };

  const mainGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "20px",
    width: "100%",
  };

  const card = {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "20px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    width: "100%",
    boxSizing: "border-box",
  };

  const sectionTitle = {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1e293b",
    marginBottom: "18px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    paddingBottom: "12px",
    borderBottom: "2px solid #f1f5f9",
  };

  const sectionIcon = {
    color: "#2563eb",
    fontSize: "16px",
  };

  const distributionItem = {
    borderBottom: "1px solid #f8fafc",
    padding: "16px 0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  const distributionName = {
    fontSize: "15px",
    fontWeight: "600",
    color: "#111827",
  };

  const distributionCount = {
    fontSize: "16px",
    fontWeight: "700",
    color: "#3b82f6",
  };

  const statusItem = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 0",
    borderBottom: "1px solid #f8fafc",
  };

  const statusLabel = {
    fontSize: "14px",
    color: "#374151",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const statusValue = {
    fontSize: "14px",
    fontWeight: "600",
    color: "#111827",
  };

  const engagementMetric = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 0",
    borderBottom: "1px solid #f8fafc",
  };

  const engagementLabel = {
    fontSize: "14px",
    color: "#374151",
  };

  const engagementValue = {
    fontSize: "14px",
    fontWeight: "600",
    color: "#111827",
  };

  const loadingContainer = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
    fontSize: "16px",
    color: "#6b7280",
    gap: "15px",
  };

  const errorContainer = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    padding: "20px",
    textAlign: "center",
    gap: "15px",
  };

  const errorIcon = {
    fontSize: "48px",
    color: "#ef4444",
  };

  const errorText = {
    color: "#ef4444",
    fontSize: "16px",
    maxWidth: "500px",
    lineHeight: "1.5",
  };

  const retryButton = {
    backgroundColor: "#2563eb",
    color: "white",
    border: "none",
    padding: "10px 20px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return "0";
    return num.toString();
  };

  const formatPercentage = (num) => {
    if (num === undefined || num === null) return "0%";
    return `${num.toFixed(2)}%`;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "#f59e0b",
      approved: "#10b981",
      suspended: "#ef4444",
      rejected: "#6b7280"
    };
    return colors[status] || "#6b7280";
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: FaClock,
      approved: FaCheckCircle,
      suspended: FaExclamationTriangle,
      rejected: FaExclamationTriangle
    };
    return icons[status] || FaUsers;
  };

  const responsiveStyles = `
    @media (min-width: 768px) {
      .dashboard-content-wrapper {
        padding: 25px;
        max-width: 1200px;
        margin: 0 auto;
      }
      
      .dashboard-stat-grid {
        grid-template-columns: repeat(4, 1fr);
        gap: 20px;
        margin-bottom: 35px;
      }
      
      .dashboard-stat-card {
        padding: 25px;
        min-height: 160px;
      }
      
      .dashboard-stat-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      }
      
      .dashboard-stat-value {
        font-size: 28px;
      }
      
      .dashboard-main-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 25px;
      }
      
      .dashboard-card {
        padding: 25px;
      }
    }
    
    @media (max-width: 767px) {
      .dashboard-stat-grid {
        grid-template-columns: repeat(2, 1fr);
      }
    }
    
    @media (max-width: 480px) {
      .dashboard-stat-grid {
        grid-template-columns: 1fr;
      }
    }
  `;

  if (loading) {
    return (
      <div style={container}>
        <div style={contentWrapper}>
          <div style={loadingContainer}>
            <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '32px' }} />
            <div>Loading dashboard data...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={container}>
        <div style={contentWrapper}>
          <div style={errorContainer}>
            <FaExclamationTriangle style={errorIcon} />
            <div style={errorText}>{error}</div>
            <button 
              style={retryButton} 
              onClick={fetchDashboardData}
            >
              <FaSync /> Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div style={container}>
        <div style={contentWrapper}>
          <div style={errorContainer}>
            <FaExclamationTriangle style={errorIcon} />
            <div style={errorText}>No data available</div>
            <button 
              style={retryButton} 
              onClick={fetchDashboardData}
            >
              <FaSync /> Refresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { summary, recent_activity, geographic_distribution, engagement, metadata } = dashboardData;

  return (
    <>
      <style>{responsiveStyles}</style>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      
      <div style={container} className="dashboard-container">
        <div style={contentWrapper} className="dashboard-content-wrapper">
          <div style={header}>
            <h2 style={title} className="dashboard-title">State Administration Dashboard</h2>
            <p style={subtitle} className="dashboard-subtitle">
              Welcome, {user?.full_name || 'State Administrator'}! Manage {metadata?.jurisdiction?.state || 'your state'} operations
            </p>
            {metadata?.generated_at && (
              <p style={{...subtitle, fontSize: '12px', marginTop: '5px'}}>
                Last updated: {new Date(metadata.generated_at).toLocaleString()}
              </p>
            )}
          </div>

          <div style={statGrid} className="dashboard-stat-grid">
            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>Total Members</span>
                <FaUsers color="#2563eb" />
              </div>
              <div style={statValue}>{formatNumber(summary?.total_members)}</div>
              <div style={statSubValue}>
                {formatNumber(summary?.approved_members)} approved
              </div>
            </div>

            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>Pending Approvals</span>
                <FaClock color="#f59e0b" />
              </div>
              <div style={statValue}>{formatNumber(summary?.pending_approvals)}</div>
              <div style={statSubValue}>
                Approval rate: {formatPercentage(summary?.approval_rate)}
              </div>
            </div>

            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>Active Users</span>
                <FaUserCheck color="#10b981" />
              </div>
              <div style={statValue}>{formatNumber(engagement?.active_users)}</div>
              <div style={statSubValue}>
                {formatNumber(recent_activity?.new_registrations_last_30_days)} new last 30 days
              </div>
            </div>

            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>Group Chats</span>
                <FaComments color="#8b5cf6" />
              </div>
              <div style={statValue}>{formatNumber(engagement?.group_chats?.total_groups)}</div>
              <div style={statSubValue}>
                {formatNumber(engagement?.messages?.total_messages)} total messages
              </div>
            </div>
          </div>

          <div style={mainGrid} className="dashboard-main-grid">
            <div style={card} className="dashboard-card">
              <h3 style={sectionTitle} className="dashboard-section-title">
                <FaMapMarkerAlt style={sectionIcon} />
                Geographic Distribution
                <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>
                  {geographic_distribution?.level}
                </span>
              </h3>

              {geographic_distribution?.distribution?.map((item, index) => (
                <div key={index} style={distributionItem} className="dashboard-distribution-item">
                  <div style={distributionName}>{item.code}</div>
                  <div style={distributionCount}>{formatNumber(item.count)} members</div>
                </div>
              ))}
            </div>

            <div style={card} className="dashboard-card">
              <h3 style={sectionTitle} className="dashboard-section-title">
                <FaChartBar style={sectionIcon} />
                Member Status Overview
              </h3>

              {recent_activity?.by_status && Object.entries(recent_activity.by_status).map(([status, count]) => {
                const StatusIcon = getStatusIcon(status);
                return (
                  <div key={status} style={statusItem} className="dashboard-status-item">
                    <div style={statusLabel}>
                      <StatusIcon color={getStatusColor(status)} />
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </div>
                    <div style={statusValue}>{formatNumber(count)}</div>
                  </div>
                );
              })}
            </div>

            <div style={card} className="dashboard-card">
              <h3 style={sectionTitle} className="dashboard-section-title">
                <FaBullhorn style={sectionIcon} />
                Engagement Metrics
              </h3>

              <div style={engagementMetric}>
                <div style={engagementLabel}>Announcements Created</div>
                <div style={engagementValue}>{formatNumber(engagement?.announcements?.total_created)}</div>
              </div>

              <div style={engagementMetric}>
                <div style={engagementLabel}>Total Recipients</div>
                <div style={engagementValue}>{formatNumber(engagement?.announcements?.total_recipients)}</div>
              </div>

              <div style={engagementMetric}>
                <div style={engagementLabel}>Delivery Rate</div>
                <div style={engagementValue}>{formatPercentage(engagement?.announcements?.delivery_rate)}</div>
              </div>

              <div style={engagementMetric}>
                <div style={engagementLabel}>Read Rate</div>
                <div style={engagementValue}>{formatPercentage(engagement?.announcements?.read_rate)}</div>
              </div>

              <div style={engagementMetric}>
                <div style={engagementLabel}>Total Messages</div>
                <div style={engagementValue}>{formatNumber(engagement?.messages?.total_messages)}</div>
              </div>
            </div>

            <div style={card} className="dashboard-card">
              <h3 style={sectionTitle} className="dashboard-section-title">
                <FaHistory style={sectionIcon} />
                System Information
              </h3>

              <div style={engagementMetric}>
                <div style={engagementLabel}>Jurisdiction Level</div>
                <div style={engagementValue}>{metadata?.jurisdiction?.level}</div>
              </div>

              <div style={engagementMetric}>
                <div style={engagementLabel}>State</div>
                <div style={engagementValue}>{metadata?.jurisdiction?.state}</div>
              </div>

              <div style={engagementMetric}>
                <div style={engagementLabel}>State Code</div>
                <div style={engagementValue}>{metadata?.jurisdiction?.state_code}</div>
              </div>

              <div style={engagementMetric}>
                <div style={engagementLabel}>Data Generated</div>
                <div style={engagementValue}>
                  {metadata?.generated_at ? new Date(metadata.generated_at).toLocaleTimeString() : 'N/A'}
                </div>
              </div>

              {/* <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                <button 
                  onClick={fetchDashboardData}
                  style={{
                    width: '100%',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    padding: '12px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <FaSync /> Refresh Dashboard
                </button>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StateDashboard;